'use client';

import { GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import dynamic from 'next/dynamic';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { ProviderType } from '@/reporting';

// 预加载 Tabs，使用 ssr: true 避免加载延迟
const SideNavTabs = dynamic(() => import('./SideNavTabs.pc'), { ssr: true });
const SideNavDropdownSSR = dynamic(() => import('./SideNavDropdown.pc'), { ssr: true });

interface SideNavWrapperProps {
  navData: GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>;
  isDarkMode: boolean;
  isBot: boolean;
}

const SideNavWrapper: FC<SideNavWrapperProps> = ({ navData, isDarkMode }) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(-1);
  const [showDropdown, setShowDropdown] = useState(false);
  const [shouldRender, setShouldRender] = useState(true);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkWidth = () => {
      setShouldRender(window.innerWidth > 1280);
    };
    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => {
      window.removeEventListener('resize', checkWidth);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleTabMouseEnter = useCallback(
    (index: number) => {
      if (index === activeTabIndex) return;
      clearHideTimeout();
      setActiveTabIndex(index);
      setShowDropdown(true);
      const newActiveNavItem = navData[index];
      const hasChildren = newActiveNavItem?.children?.length > 0;
      if (hasChildren) {
        trackEvent(
          {
            eventName: 'TopNavigation_Exp',
            properties: {
              page_name: cnName,
              url_path: window.location.href,
              tab_name: newActiveNavItem.content?.nav_name,
              tab_index: index,
            },
          },
          [ProviderType.SENSORS],
        );
      }
    },
    [activeTabIndex, navData, cnName, trackEvent],
  );

  const handleTabMouseLeave = useCallback(() => {
    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      setShowDropdown(false);
      setActiveTabIndex(-1);
    }, 100);
  }, []);

  const handleDropdownMouseEnter = useCallback(() => {
    clearHideTimeout();
    setShowDropdown(true);
  }, []);

  const handleDropdownMouseLeave = useCallback(() => {
    clearHideTimeout();
    setShowDropdown(false);
    setActiveTabIndex(-1);
  }, []);

  const handleSubnodeClick = useCallback(() => {
    setShowDropdown(false);
  }, []);

  if (!shouldRender) return null;

  return (
    <>
      {/* Tabs: 统一使用 SideNavTabs，它内部处理水合状态 */}
      <SideNavTabs
        navData={navData}
        isDarkMode={isDarkMode}
        onTabMouseEnter={handleTabMouseEnter}
        onTabMouseLeave={handleTabMouseLeave}
        containerRef={containerRef}
        onSubnodeClick={handleSubnodeClick}
      />
      {/* Dropdown: 默认SSR，保证二/三级菜单链接进入初始HTML */}
      <SideNavDropdownSSR
        navData={navData}
        isDarkMode={isDarkMode}
        activeTabIndex={activeTabIndex}
        showDropdown={showDropdown}
        onDropdownMouseEnter={handleDropdownMouseEnter}
        onDropdownMouseLeave={handleDropdownMouseLeave}
      />
    </>
  );
};

export default SideNavWrapper;
